import type {MilestonePickerMilestoneItem} from '@github-ui/item-picker-milestone'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'

import type {
  updateIssueMilestoneBulkRelayMutation,
  UpdateIssuesBulkInput,
} from './__generated__/updateIssueMilestoneBulkRelayMutation.graphql'

type issueUpdateArgs = {
  milestoneId: string | undefined
  clearMilestone: boolean | undefined
}

export function updateIssuesById(store: RecordSourceProxy, ids: string[], issueUpdateArgs: issueUpdateArgs) {
  for (const id of ids) {
    const issue = store.get(id)
    if (!issue) continue

    if (issueUpdateArgs.clearMilestone) {
      issue.setValue(null, 'milestone')
    } else if (issueUpdateArgs.milestoneId) {
      const milestone = store.get(issueUpdateArgs.milestoneId)
      if (milestone) {
        issue.setLinkedRecord(milestone, 'milestone')
      }
    }
  }
}

export function addMilestoneToStore(
  store: RecordSourceProxy,
  milestoneId: string,
  milestone: MilestonePickerMilestoneItem,
) {
  // Create an optimistic Milestone record with the provided fields so
  // issues can link to it immediately, as long as it does not yet exist.
  // If it does exist, still copy the values over, as they could be stale.
  const record = store.get(milestoneId) || store.create(milestoneId, 'Milestone')
  // Ensure the id is set (create already associates the id but being explicit is safe)
  record.setValue(milestoneId, 'id')

  if (milestone.title != null) {
    record.setValue(milestone.title, 'title')
  }
  if (milestone.closed != null) {
    record.setValue(milestone.closed, 'closed')
  }
  if (milestone.dueOn != null) {
    record.setValue(milestone.dueOn, 'dueOn')
  }
  if (milestone.progressPercentage != null) {
    // progressPercentage is a number
    record.setValue(milestone.progressPercentage, 'progressPercentage')
  }
  if (milestone.url != null) {
    record.setValue(milestone.url, 'url')
  }
  if (milestone.closedAt != null) {
    record.setValue(milestone.closedAt, 'closedAt')
  }
}

export function commitUpdateIssueMilestoneBulkMutation({
  environment,
  input: {ids, milestoneId, clearMilestone},
  onCompleted,
  onError,
}: {
  environment: Environment
  input: {ids: string[]} & issueUpdateArgs
  onCompleted?: (response: updateIssueMilestoneBulkRelayMutation['response']) => void
  onError?: (error: Error) => void
}) {
  const inputArgs = {milestoneId, clearMilestone}
  const inputHash: UpdateIssuesBulkInput = {
    ids,
    ...inputArgs,
  }

  return commitMutation<updateIssueMilestoneBulkRelayMutation>(environment, {
    mutation: graphql`
      mutation updateIssueMilestoneBulkRelayMutation($input: UpdateIssuesBulkInput!) @raw_response_type {
        updateIssuesBulk(input: $input) {
          jobId
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    updater: store => updateIssuesById(store, ids, inputArgs),
    optimisticUpdater: store => updateIssuesById(store, ids, inputArgs),
    onCompleted: response => onCompleted && onCompleted(response),
    onError: error => onError && onError(error),
  })
}
